<template>
  <div :class="b()">
    <div
      ref="container"
      :class="b('container')"
    ></div>
  </div>
</template>

<script lang="ts">
  import {
    defineComponent,
    PropType,
    Ref,
    ref,
  } from 'vue';
  import googleMapsApi from '@/compositions/google-maps-api';
  import useSessionStore from '@/stores/session';
  import spritePath from '@/assets/icons.svg';

  type Setup = {
    container: Ref<HTMLDivElement>;
    googleMapsApi: ReturnType<typeof googleMapsApi>;
    sessionStore: ReturnType<typeof useSessionStore>;
  }

  type Data = {

    /**
     * Holds the related Google Maps instance.
     */
    mapInstance: google.maps.Map | null;

    /**
     * Holds a list of map markers.
     */
    markers: google.maps.Marker[];

    /**
     * Holds the debounce timeout for the bounding.
     */
    boundingTimeout: ReturnType<typeof setTimeout> | null;

    /**
     * Determines if the component is allowed to auto update the bounding.
     */
    allowAutoUpdates: boolean;

    /**
     * Holds the geocoder instance.
     */
    geocoder?: google.maps.Geocoder;

    infoWindows: google.maps.InfoWindow[];

    tempMarker: google.maps.Marker[];
  }

  export interface GoogleMapsLocation {
    lat?: string | number | null;
    lng?: string | number | null;
    geocode?: string;
    icon?: string;
    title?: string;
    infoWindow?: Promise<Element | string>;
  }

  interface EventClick {
    location: GoogleMapsLocation;
    marker: google.maps.Marker;
  }

  const THEME_01: google.maps.MapTypeStyle[] = [
    {
      elementType: 'geometry',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      featureType: 'administrative.country',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#b0b7ba',
        },
        {
          weight: 2,
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dadada',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#c9c9c9',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#b2d5eb',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
  ];

  const THEME_02: google.maps.MapTypeStyle[] = [
    {
      elementType: 'geometry',
      stylers: [
        {
          saturation: -5,
        },
      ],
    },
    {
      elementType: 'labels.icon',
      stylers: [
        {
          visibility: 'off',
        },
      ],
    },
    {
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      elementType: 'labels.text.stroke',
      stylers: [
        {
          color: '#f5f5f5',
        },
      ],
    },
    {
      featureType: 'administrative.country',
      elementType: 'geometry.stroke',
      stylers: [
        {
          color: '#b0b7ba',
        },
        {
          weight: 2,
        },
      ],
    },
    {
      featureType: 'administrative.land_parcel',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#bdbdbd',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'poi',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'geometry',
      stylers: [
        {
          saturation: -15,
        },
        {
          lightness: 5,
        },
      ],
    },
    {
      featureType: 'poi.park',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [
        {
          color: '#ffffff',
        },
      ],
    },
    {
      featureType: 'road.arterial',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#757575',
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'geometry',
      stylers: [
        {
          color: '#dec317',
        },
        {
          saturation: -40,
        },
        {
          lightness: 55,
        },
      ],
    },
    {
      featureType: 'road.highway',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#616161',
        },
      ],
    },
    {
      featureType: 'road.local',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
    {
      featureType: 'transit.line',
      elementType: 'geometry',
      stylers: [
        {
          color: '#e5e5e5',
        },
      ],
    },
    {
      featureType: 'transit.station',
      elementType: 'geometry',
      stylers: [
        {
          color: '#eeeeee',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [
        {
          color: '#c9c9c9',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'geometry.fill',
      stylers: [
        {
          color: '#b2d5eb',
        },
      ],
    },
    {
      featureType: 'water',
      elementType: 'labels.text.fill',
      stylers: [
        {
          color: '#9e9e9e',
        },
      ],
    },
  ];

  /**
   * Renders a Google Map.
   */
  export default defineComponent({
    name: 'c-google-maps',

    // components: {},

    props: {
      /**
       * Expects a list of locations (markers). You can define additional properties if required.
       *
       * lat/lng OR geocode MUST be defined!
       */
      locations: {
        type: Array as PropType<GoogleMapsLocation[]>,
        required: true,
      },

      /**
       * Allows to use an alternative map theme.
       *
       * Valid values: `[default, gray]`
       */
      theme: {
        type: String,
        default: undefined,
        validator(value: string) {
          return [
            'theme-01',
            'theme-02',
          ].includes(value);
        },
      },

      /**
       * Allows enabling/disabling auto bounding.
       */
      bounds: {
        type: Boolean,
        default: true,
      },

      /**
       * Allows passing custom bounds.
       *
       * @see https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds
       */
      customBounds: {
        type: Object as PropType<google.maps.LatLngBounds>,
        default: null,
      },

      /**
       * Allows to set a size for custom icons.
       */
      iconSize: {
        type: Number,
        default: 40,
      },

      /**
       * Allows defining additional styles for the current theme. e.g. to hide business POIs
       */
      styles: {
        type: Array as PropType<google.maps.MapTypeStyle[]>,
        default: () => [],
      },

      /**
       * Allows to specify a custom Google Maps config.
       *
       * @see https://developers.google.com/maps/documentation/javascript/reference/map
       */
      mapsConfig: {
        type: Object as PropType<google.maps.MapOptions>,
        default: null,
      },

      /**
       * Gets the position of the user which centers the map and adds a marker to the position.
       */
      userPosition: {
        type: Object as PropType<google.maps.LatLng>,
        default: null,
      },

      /**
       * Defines if there is a click listener on the map to set a custom marker.
       */
      hasClickListener: {
        type: Boolean,
        default: true,
      },

      /**
       * Defines if the map markers should be clickable.
       */
      clickableMarkers: {
        type: Boolean,
        default: true,
      },
    },
    emits: {
      markerClick: (payload: EventClick) => !!(payload?.location && payload?.marker),
      mapClick: (payload: google.maps.LatLng) => !!payload,
    },

    setup(): Setup {
      const container = ref();

      return {
        container,
        googleMapsApi: googleMapsApi(),
        sessionStore: useSessionStore(),
      };
    },
    data(): Data {
      return {
        mapInstance: null,
        markers: [],
        boundingTimeout: null,
        allowAutoUpdates: true,
        geocoder: undefined,
        infoWindows: [],
        tempMarker: [],
      };
    },

    computed: {
      /**
       * Returns a style definition based on the given theme.
       */
      mapStyles(): google.maps.MapTypeStyle[] {
        switch (this.theme || this.sessionStore.theme) {
          case 'theme-02':
            return [...THEME_02, ...this.styles];

          default:
            return [...THEME_01, ...this.styles];
        }
      },
    },
    watch: {
      mapsConfig(config: google.maps.MapOptions): void {
        if (config?.center) {
          this.mapInstance?.setCenter(config.center);
          this.mapInstance?.setZoom(11);
        }
      },

      userPosition(position: google.maps.LatLng): void {
        if (position) {
          this.onClickMap(position);
        } else {
          this.resetTempMarker();
        }
      },

      locations(): void {
        if (this.mapInstance) {
          this.updateMarkers();
        }
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    mounted() {
      this.googleMapsApi.then((maps) => {
        this.geocoder = new maps.Geocoder();

        this.$nextTick(() => {
          this.createMapInstance();
        });
      });

      this.$el.addEventListener('mousedown', this.disableAutoUpdates, { once: true, passive: true });
    },
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    beforeUnmount() {
      this.$el.removeEventListener('mousedown', this.disableAutoUpdates);
    },
    // unmounted() {},

    methods: {
      /**
       * Updates the 'allowAutoUpdates' flag.
       */
      disableAutoUpdates(): void {
        this.allowAutoUpdates = false;
      },

      /**
       * Initialize the google maps with the default values.
       */
      createMapInstance(): void {
        if (!this.locations?.length && !this.mapsConfig?.center) {
          const errorMsg = 'Neither locations nor a center coordinate was given. At least one of them is needed to create a Google Maps.'; // eslint-disable-line vue/max-len

          throw new Error(errorMsg);
        }

        this.mapInstance = new window.google.maps.Map(this.container, {
          // Fallbacks
          maxZoom: 18,
          zoom: 12,

          // Static
          center: this.mapsConfig?.center || { lat: 0, lng: 0 },
          styles: this.mapStyles,
          draggableCursor: 'crosshair',

          // Custom
          ...this.mapsConfig,
        });

        this.createMarkersForLocations();

        if (this.hasClickListener) {
          this.mapInstance.addListener('click', (event: google.maps.KmlMouseEvent) => this.onClickMap(event.latLng || undefined));
        }

        // Make sure something is visible.
        if (this.bounds) {
          this.fitBounds();
        } else if (!this.mapsConfig?.center && this.markers[0]) {
          const markerPosition = this.markers[0].getPosition();

          if (markerPosition) {
            this.mapInstance?.setCenter(markerPosition);
          }
        }
      },

      /**
       * Maps custom icons for the Google Maps. If no source is given, the default icon will be used.
       */
      mapIcon(iconSrc: string): google.maps.Icon|google.maps.Symbol | null {
        if (!iconSrc || typeof iconSrc !== 'string') {
          return null;
        }

        const { iconSize } = this;

        return {
          url: iconSrc,
          scaledSize: iconSize ? new window.google.maps.Size(iconSize, iconSize) : null,
          anchor: new google.maps.Point(0, 40),
        };
      },

      /**
       * Updates the markers on the map for the component locations.
       */
      createMarkersForLocations(fitBounds = true): void {
        this.locations.forEach((location) => {
          if (!location.lat || !location.lng) {
            this.createMarkerFromAddress(location);

            return;
          }

          const marker = this.createMarker(location);

          this.createInfoWindow(location, marker);
        });

        if (this.bounds && this.markers.length && fitBounds) {
          this.fitBounds();
        }
      },

      /**
       * Try to create a marker from an address string.
       *
       * @param {Object} location - A location object.
       * @param {String} location.geocode - An address string, that will be used to calculate the coordinates.
       */
      createMarkerFromAddress(location: GoogleMapsLocation): void {
        if (!location.geocode) {
          throw new Error("Unable to geocode a location without 'geocode' information.");
        }

        this.geocoder?.geocode({ address: location.geocode }, (results, status) => {
          if (status === 'OK') {
            const fitBounds = this.allowAutoUpdates && !this.mapsConfig?.center && this.bounds !== false;

            if (!results?.length) {
              return;
            }

            this.createMarker({
              ...location,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
            });

            if (fitBounds) {
              this.fitBounds();
            }
          } else {
            throw new Error(`Geocode was not successful for the following reason: ${status}`);
          }
        });
      },

      /**
       * Creates a new marker on the current map.
       *
       * @returns {Property.Marker}
       */
      createMarker(location: GoogleMapsLocation): google.maps.Marker {
        const marker = new window.google.maps.Marker({
          position: {
            lat: location.lat,
            lng: location.lng,
          },
          map: this.mapInstance,
          icon: location.icon ? this.mapIcon(location.icon) : null,
          title: location.title,
          clickable: this.clickableMarkers,
        } as google.maps.MarkerOptions);

        marker.addListener('click', () => {
          this.$emit('markerClick', { location, marker });
        });

        this.markers.push(marker);

        return marker;
      },

      createInfoWindow(location: GoogleMapsLocation, marker: google.maps.Marker): void {
        if (!location.infoWindow) {
          return;
        }

        location.infoWindow.then((infoWindowContent) => {
          const infoWindow = new window.google.maps.InfoWindow({
            content: infoWindowContent,
          });

          this.infoWindows.push(infoWindow);

          window.google.maps.event.addListener(marker, 'mouseover', () => {
            this.infoWindows.forEach(item => item.close());
            infoWindow.open({ anchor: marker, map: this.mapInstance });
          });
        });
      },

      /**
       * Function zooms the map to fit all the pointers on the map.
       */
      fitBounds(): void {
        if (this.boundingTimeout) {
          clearTimeout(this.boundingTimeout);
        }

        this.boundingTimeout = setTimeout(() => {
          const { customBounds } = this;

          if (!this.mapInstance) {
            return;
          }

          if (customBounds) {
            this.mapInstance.fitBounds(customBounds);
          } else {
            const locationsBounds = new window.google.maps.LatLngBounds();

            this.markers.forEach(marker => locationsBounds.extend(marker.getPosition() as google.maps.LatLng));
            this.mapInstance.fitBounds(locationsBounds);
          }
        }, 200);
      },

      onClickMap(latLng?: google.maps.LatLng): void {
        if (this.mapInstance && latLng) {
          this.resetTempMarker();

          // eslint-disable-next-line no-new
          this.tempMarker.push(new window.google.maps.Marker({
            position: latLng,
            map: this.mapInstance,
            icon: {
              url: `${spritePath}#i-map-marker`,
              scaledSize: new window.google.maps.Size(30, 30),
            },
          }));

          this.mapInstance?.panTo(latLng);
          this.mapInstance?.setZoom(11);
          this.$emit('mapClick', latLng);
        }
      },

      resetTempMarker(): void {
        this.tempMarker.forEach((marker) => {
          marker.setMap(null);
          marker.setVisible(false);
        });
        this.tempMarker = [];
      },

      updateMarkers(): void {
        this.resetLocationMarkers();
        this.createMarkersForLocations(false);
      },

      resetLocationMarkers(): void {
        this.markers.forEach((marker) => {
          marker.setMap(null);
          marker.setVisible(false);
        });
        this.markers = [];
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '../setup/scss/variables';

  .c-google-maps {
    height: 50vh;

    &__container {
      height: 100%;
    }

    .gm-style .gm-style-iw-c {
      border-radius: 0;
    }

    .gm-style .gm-style-mtc li {
      display: flex;
      grid-column-gap: variables.$spacing--10;
      align-items: center;
    }
  }
</style>
